import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Badge, Form, InputGroup, Modal, Row, Col, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEye, FaCalendarAlt, FaTrash } from 'react-icons/fa';
import { getEstimates, deleteEstimate } from '../../services/estimateService';
import ScheduleEstimateModal from './ScheduleEstimateModal';
import './EstimateList.css';

const STATUS_META = {
  pending: { bg: 'warning', label: 'Pending' },
  scheduled: { bg: 'success', label: 'Scheduled' },
  in_progress: { bg: 'danger', label: 'In Progress' },
  'in-progress': { bg: 'danger', label: 'In Progress' },
  converted: { bg: 'primary', label: 'Converted' },
  approved: { bg: 'info', label: 'Approved' },
  complete: { bg: 'success', label: 'Complete' },
};

const titleCase = (s) =>
  String(s || '')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const EstimateList = ({ onSelectEstimate, refreshTrigger }) => {
  const [estimates, setEstimates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Scheduling modal
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState(null);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const loadEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEstimates();
      setEstimates(data);
    } catch (error) {
      console.error('Error loading estimates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEstimates();
  }, [loadEstimates, refreshTrigger]);

  const filteredEstimates = useMemo(() => {
    let items = [...estimates];

    if (statusFilter !== 'all') {
      if (statusFilter === 'no-status') {
        items = items.filter(e => !e.status);
      } else {
        items = items.filter(e => e.status === statusFilter);
      }
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      items = items.filter(e =>
        (e.customer_name && e.customer_name.toLowerCase().includes(q)) ||
        (e.site_address && e.site_address.toLowerCase().includes(q)) ||
        (e.site_name && e.site_name.toLowerCase().includes(q)) ||
        (`est-${e.id}`.includes(q)) ||
        (e.estimator_name && e.estimator_name.toLowerCase().includes(q))
      );
    }

    return items;
  }, [estimates, searchTerm, statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const d = new Date(dateString);
    return {
      date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
  };

  const getStatusBadge = (estimate) => {
    if (!estimate.status) return { bg: 'secondary', label: 'New' };
    const meta = STATUS_META[estimate.status];
    if (meta) return meta;
    return { bg: 'secondary', label: titleCase(estimate.status) };
  };

  const renderScheduledInfo = (estimate) => {
    if (!estimate.scheduled_date) {
  return (
    <Badge bg="secondary" className="estimate-list__scheduled">
      <FaCalendarAlt className="estimate-list__scheduledIcon" />
      <span className="estimate-list__scheduledText">
        <span>Not Scheduled</span>
        <small>&nbsp;</small>
      </span>
    </Badge>
  );
}


    const { date, time } = formatDateTime(estimate.scheduled_date);
    return (
      <Badge bg="success" className="estimate-list__scheduled">
        <FaCalendarAlt className="estimate-list__scheduledIcon" />
        <span className="estimate-list__scheduledText">
          <span>{date}</span>
          <small>{time} ({estimate.estimator_name})</small>
        </span>
      </Badge>
    );
  };

  const getStatusOptions = () => {
    const statusCounts = estimates.reduce((acc, e) => {
      const status = e.status || 'no-status';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const options = [{ value: 'all', label: `All (${estimates.length})` }];

    if (statusCounts['no-status']) {
      options.push({ value: 'no-status', label: `New/No Status (${statusCounts['no-status']})` });
    }

    Object.entries(statusCounts)
      .filter(([s]) => s !== 'no-status')
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([status, count]) => options.push({ value: status, label: `${titleCase(status)} (${count})` }));

    return options;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const handleScheduleClick = (estimate) => {
    setSelectedEstimateId(estimate.id);
    setShowScheduleModal(true);
  };

  const handleEstimateScheduled = (scheduledEstimate) => {
    setEstimates(prev => prev.map(e => (e.id === scheduledEstimate.id ? scheduledEstimate : e)));
    setShowScheduleModal(false);
    setSelectedEstimateId(null);
  };

  const handleDeleteClick = (estimate) => {
    setEstimateToDelete(estimate);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!estimateToDelete) return;
    setIsDeleting(true);
    setDeleteError('');

    try {
      await deleteEstimate(estimateToDelete.id);
      setEstimates(prev => prev.filter(e => e.id !== estimateToDelete.id));
      setShowDeleteModal(false);
      setEstimateToDelete(null);
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete estimate');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <div className="estimate-list">Loading estimates...</div>;

  return (
    <div className="estimate-list">
      <div className="estimate-list__toolbar">
        <Row className="g-2">
          <Col md={6}>
            <InputGroup>
              <Form.Control
                className="estimate-list__search"
                type="text"
                placeholder="Search by ID, Customer, Site, Estimator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col md={4}>
            <Form.Select
              className="estimate-list__filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {getStatusOptions().map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Button
              className="estimate-list__clearBtn"
              variant="secondary"
              onClick={clearFilters}
              disabled={searchTerm === '' && statusFilter === 'all'}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>

        <div className="estimate-list__summary">
          <small className="text-muted">
            Showing {filteredEstimates.length} of {estimates.length}
            {searchTerm && ` matching "${searchTerm}"`}
            {statusFilter !== 'all' && ` with status: ${statusFilter === 'no-status' ? 'No Status' : statusFilter}`}
          </small>
        </div>
      </div>

      {filteredEstimates.length === 0 ? (
        <div className="estimate-list__empty">
          <p className="mb-2">No estimates found.</p>
          {(searchTerm || statusFilter !== 'all') && (
            <Button variant="link" onClick={clearFilters}>Clear filters to see all estimates</Button>
          )}
        </div>
      ) : (
        <Table striped hover responsive className="estimate-table estimate-list__table">
          <thead>
            <tr>
              <th>Offers</th>
              <th>Customer</th>
              <th>Address</th>
              <th>Status</th>
              <th>Date Created</th>
              <th>Scheduled Date</th>
              <th className="estimate-list__actionsHead">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEstimates.map(estimate => {
              const status = getStatusBadge(estimate);

              return (
                <tr key={estimate.id} onClick={onSelectEstimate ? () => onSelectEstimate(estimate) : undefined}>
                  <td data-label="Offers">{estimate.reference_number || `EST-${estimate.id}`}</td>
                  <td data-label="Customer">{estimate.customer_name}</td>
                  <td data-label="Address">{estimate.site_address}</td>
                  <td data-label="Status">
                    <Badge bg={status.bg} className="status-badge">{status.label}</Badge>
                  </td>
                  <td data-label="Date Created">{formatDate(estimate.created_at)}</td>
                  <td data-label="Scheduled Date">{renderScheduledInfo(estimate)}</td>
                  <td data-label="Actions" className="actions-column estimate-list__actionsCell">
                    <div className="estimate-list__actions">
                      <Link to={`/estimates/${estimate.id}/progress`} onClick={(e) => e.stopPropagation()}>
                        <Button className="estimate-list__btn" variant="primary" size="sm">
                          <FaEye /> <span className="estimate-list__btnText">View</span>
                        </Button>
                      </Link>

                      <Button
                        className="estimate-list__btn estimate-list__btn--schedule"
                        variant={estimate.scheduled_date ? 'secondary' : 'success'}
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleScheduleClick(estimate); }}
                      >
                        <FaCalendarAlt className="me-1" />
                        <span className="estimate-list__btnText">
                          {estimate.scheduled_date ? 'Reschedule' : 'Schedule'}
                        </span>
                      </Button>

                      <Button
                        className="estimate-list__iconBtn"
                        variant="danger"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(estimate); }}
                        title="Delete Estimate"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <ScheduleEstimateModal
        show={showScheduleModal}
        estimateId={selectedEstimateId}
        onClose={() => { setShowScheduleModal(false); setSelectedEstimateId(null); }}
        onScheduled={handleEstimateScheduled}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deleteError && <Alert variant="danger">{deleteError}</Alert>}

          {estimateToDelete && (
            <>
              <p>Are you sure you want to delete this estimate?</p>

              <div className="mt-3 p-3 bg-light rounded">
                <strong>Estimate Details:</strong>
                <ul className="mb-0 mt-2">
                  <li>ID: EST-{estimateToDelete.id}</li>
                  <li>Customer: {estimateToDelete.customer_name}</li>
                  <li>Site: {estimateToDelete.site_name || estimateToDelete.site_address}</li>
                  {estimateToDelete.scheduled_date && (
                    <li>
                      Scheduled: {formatDateTime(estimateToDelete.scheduled_date).date} {formatDateTime(estimateToDelete.scheduled_date).time}
                    </li>
                  )}
                </ul>
              </div>

              <Alert variant="warning" className="mt-3">
                <strong>Warning:</strong> This action cannot be undone.
                {estimateToDelete.status === 'scheduled' && ' This estimate is currently scheduled.'}
              </Alert>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete Estimate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EstimateList;
